// Works out the simple formulas spreadsheet makes use (=SUM(B2:B6), =B2*C2, ...) so the
// preview can show their values. The downloaded .xlsx keeps the real formulas.
// Cells are addressed like Excel: the header is row 1 and the first column is A.

const FUNCTIONS = {
  SUM: (values) => values.reduce((total, value) => total + value, 0),
  AVERAGE: (values) =>
    values.length
      ? values.reduce((total, value) => total + value, 0) / values.length
      : NaN,
  MIN: (values) => Math.min(...values),
  MAX: (values) => Math.max(...values),
  COUNT: (values) => values.length,
  ROUND: ([value, places = 0]) => {
    const factor = 10 ** places;
    return Math.round(value * factor) / factor;
  },
  ABS: ([value]) => Math.abs(value),
};

export function isFormula(text) {
  return typeof text === "string" && /^\s*=\s*\S/.test(text);
}

export function toNumber(text) {
  const plain = String(text ?? "")
    .replace(/\*\*/g, "")
    .replace(/[£$€,\s]/g, "");
  if (!/^-?\d*\.?\d+%?$/.test(plain)) return null;
  return plain.endsWith("%") ? Number(plain.slice(0, -1)) / 100 : Number(plain);
}

function columnIndex(letters) {
  return (
    [...letters].reduce(
      (index, letter) => index * 26 + letter.charCodeAt(0) - 64,
      0
    ) - 1
  );
}

function tokenize(formula) {
  const tokens = [];
  const pattern =
    /\s*(?:([A-Z]+\d+(?::[A-Z]+\d+)?)|(\d*\.?\d+%?)|([A-Z]+)(?=\s*\()|(.))/gy;
  let match;
  while ((match = pattern.exec(formula)) && match[0] !== "") {
    if (match[1]) tokens.push({ type: "ref", value: match[1] });
    else if (match[2])
      tokens.push({ type: "number", value: toNumber(match[2]) });
    else if (match[3]) tokens.push({ type: "function", value: match[3] });
    else if (match[4].trim()) tokens.push({ type: "symbol", value: match[4] });
    if (pattern.lastIndex >= formula.length) break;
  }
  return tokens;
}

// rows: every row of the table including the header, as strings
export function evaluateTable(rows) {
  const cache = new Map();

  const cellValue = (row, column, seen) => {
    const key = `${row}:${column}`;
    if (cache.has(key)) return cache.get(key);
    if (seen.has(key)) return NaN;
    const text = rows[row]?.[column];
    let value = null;
    if (isFormula(text)) {
      value = evaluate(text, new Set([...seen, key]));
    } else {
      value = toNumber(text);
    }
    cache.set(key, value);
    return value;
  };

  const reference = (ref, seen) => {
    const [, letters, digits] = /^([A-Z]+)(\d+)$/.exec(ref);
    return cellValue(Number(digits) - 1, columnIndex(letters), seen);
  };

  const range = (ref, seen) => {
    const [from, to] = ref.split(":");
    const [, fromLetters, fromDigits] = /^([A-Z]+)(\d+)$/.exec(from);
    const [, toLetters, toDigits] = /^([A-Z]+)(\d+)$/.exec(to);
    const values = [];
    for (
      let row = Number(fromDigits) - 1;
      row <= Number(toDigits) - 1;
      row += 1
    ) {
      for (
        let column = columnIndex(fromLetters);
        column <= columnIndex(toLetters);
        column += 1
      ) {
        const value = cellValue(row, column, seen);
        if (typeof value === "number" && !Number.isNaN(value))
          values.push(value);
      }
    }
    return values;
  };

  function evaluate(formula, seen) {
    const tokens = tokenize(formula.trim().replace(/^=/, "").toUpperCase());
    let position = 0;
    const peek = () => tokens[position];
    const take = () => tokens[position++];
    const expect = (value) => {
      const token = take();
      if (!token || token.value !== value) throw new Error(`Expected ${value}`);
    };

    const argument = () => {
      const token = peek();
      if (token?.type === "ref" && token.value.includes(":")) {
        take();
        return range(token.value, seen);
      }
      return [expression()];
    };

    const factor = () => {
      const token = take();
      if (!token) throw new Error("Unexpected end");
      if (token.value === "-") return -factor();
      if (token.value === "+") return factor();
      if (token.type === "number") return token.value;
      if (token.type === "ref") {
        if (token.value.includes(":"))
          throw new Error("Range outside a function");
        const value = reference(token.value, seen);
        return value ?? 0;
      }
      if (token.type === "function") {
        const fn = FUNCTIONS[token.value];
        if (!fn) throw new Error(`Unknown function ${token.value}`);
        expect("(");
        const values = [];
        if (peek()?.value !== ")") {
          values.push(...argument());
          while (peek()?.value === ",") {
            take();
            values.push(...argument());
          }
        }
        expect(")");
        return fn(values);
      }
      if (token.value === "(") {
        const value = expression();
        expect(")");
        return value;
      }
      throw new Error(`Unexpected ${token.value}`);
    };

    const term = () => {
      let value = factor();
      while (peek()?.value === "*" || peek()?.value === "/") {
        value = take().value === "*" ? value * factor() : value / factor();
      }
      return value;
    };

    function expression() {
      let value = term();
      while (peek()?.value === "+" || peek()?.value === "-") {
        value = take().value === "+" ? value + term() : value - term();
      }
      return value;
    }

    try {
      const value = expression();
      if (position !== tokens.length) return NaN;
      return value;
    } catch {
      return NaN;
    }
  }

  return (row, column) => cellValue(row, column, new Set());
}

export function formatNumber(value) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "";
  return Number.isInteger(value)
    ? value.toLocaleString("en-GB")
    : value.toLocaleString("en-GB", { maximumFractionDigits: 2 });
}

// The text to show for each cell, with formulas replaced by their values
export function displayRows(rows) {
  if (!rows.some((row) => row.some(isFormula))) return rows;
  const valueAt = evaluateTable(rows);
  return rows.map((row, rowIndex) =>
    row.map((cell, column) => {
      if (!isFormula(cell)) return cell;
      const shown = formatNumber(valueAt(rowIndex, column));
      return shown || cell;
    })
  );
}
